/**
 * Receipt template configurations for different business types
 * Customize these templates based on business requirements
 */

export interface ReceiptTemplate {
  id: string
  name: string
  description: string
  config: {
    // Header configuration
    header: {
      showLogo: boolean
      showCompanyName: boolean
      companyNameStyle: 'normal' | 'bold' | 'double'
      showAddress: boolean
      showPhone: boolean
      showEmail: boolean
      showGstin: boolean
      showWebsite: boolean
      additionalInfo?: string[]
    }
    // Invoice details configuration
    invoiceDetails: {
      showInvoiceNumber: boolean
      showDate: boolean
      dateFormat: string
      showTime: boolean
      showPaymentMode: boolean
      showSalesperson?: boolean
      showTerminal?: boolean
    }
    // Customer details configuration
    customer: {
      showCustomerName: boolean
      showCustomerPhone: boolean
      showCustomerAddress: boolean
      showCustomerGstin: boolean
      showLoyaltyPoints?: boolean
    }
    // Items configuration
    items: {
      columns: Array<'item' | 'hsn' | 'qty' | 'unit' | 'rate' | 'discount' | 'tax' | 'amount'>
      showItemCode: boolean
      showHsnCode: boolean
      showDiscountLine: boolean
      showTaxInline: boolean
      wrapItemNames: boolean
      itemNameMaxWidth: number // percentage of line width
    }
    // Totals configuration
    totals: {
      showSubtotal: boolean
      showDiscountTotal: boolean
      showTaxBreakdown: boolean
      taxBreakdownType: 'combined' | 'separate' // combined shows GST, separate shows CGST/SGST
      showRounding: boolean
      showSavings?: boolean
    }
    // Footer configuration
    footer: {
      showThankYouMessage: boolean
      thankYouText: string
      showTermsAndConditions: boolean
      termsText?: string[]
      showReturnPolicy: boolean
      returnPolicyText?: string
      showBarcode: boolean
      showQrCode: boolean
      qrCodeData?: 'invoice' | 'payment' | 'website' | 'custom'
      showSocialMedia?: boolean
      additionalFooterText?: string[]
    }
    // Paper and formatting
    formatting: {
      paperSize: '80mm' | '58mm' | '44mm'
      useEscPos: boolean
      cutPaper: boolean
      feedLines: number
      charset: string
    }
  }
}

// Default templates for common business types
export const defaultTemplates: ReceiptTemplate[] = [
  {
    id: 'retail-basic',
    name: 'Basic Retail',
    description: 'Simple receipt for retail stores',
    config: {
      header: {
        showLogo: false,
        showCompanyName: true,
        companyNameStyle: 'bold',
        showAddress: true,
        showPhone: true,
        showEmail: false,
        showGstin: true,
        showWebsite: false
      },
      invoiceDetails: {
        showInvoiceNumber: true,
        showDate: true,
        dateFormat: 'dd-MMM-yyyy',
        showTime: true,
        showPaymentMode: true
      },
      customer: {
        showCustomerName: true,
        showCustomerPhone: true,
        showCustomerAddress: false,
        showCustomerGstin: false
      },
      items: {
        columns: ['item', 'qty', 'rate', 'amount'],
        showItemCode: false,
        showHsnCode: false,
        showDiscountLine: true,
        showTaxInline: false,
        wrapItemNames: true,
        itemNameMaxWidth: 50
      },
      totals: {
        showSubtotal: true,
        showDiscountTotal: true,
        showTaxBreakdown: true,
        taxBreakdownType: 'separate',
        showRounding: false
      },
      footer: {
        showThankYouMessage: true,
        thankYouText: 'Thank You! Visit Again',
        showTermsAndConditions: false,
        showReturnPolicy: false,
        showBarcode: false,
        showQrCode: false
      },
      formatting: {
        paperSize: '80mm',
        useEscPos: false,
        cutPaper: true,
        feedLines: 3,
        charset: 'UTF-8'
      }
    }
  },
  {
    id: 'retail-gst',
    name: 'GST Compliant Retail',
    description: 'Full GST compliant receipt with all tax details',
    config: {
      header: {
        showLogo: false,
        showCompanyName: true,
        companyNameStyle: 'double',
        showAddress: true,
        showPhone: true,
        showEmail: true,
        showGstin: true,
        showWebsite: false,
        additionalInfo: ['State: Maharashtra', 'State Code: 27']
      },
      invoiceDetails: {
        showInvoiceNumber: true,
        showDate: true,
        dateFormat: 'dd/MM/yyyy',
        showTime: true,
        showPaymentMode: true
      },
      customer: {
        showCustomerName: true,
        showCustomerPhone: true,
        showCustomerAddress: true,
        showCustomerGstin: true
      },
      items: {
        columns: ['item', 'hsn', 'qty', 'rate', 'tax', 'amount'],
        showItemCode: false,
        showHsnCode: true,
        showDiscountLine: true,
        showTaxInline: true,
        wrapItemNames: true,
        itemNameMaxWidth: 40
      },
      totals: {
        showSubtotal: true,
        showDiscountTotal: true,
        showTaxBreakdown: true,
        taxBreakdownType: 'separate',
        showRounding: true
      },
      footer: {
        showThankYouMessage: true,
        thankYouText: 'Thank You for your business!',
        showTermsAndConditions: true,
        termsText: ['Subject to Mumbai Jurisdiction', 'E. & O.E.'],
        showReturnPolicy: false,
        showBarcode: false,
        showQrCode: true,
        qrCodeData: 'invoice'
      },
      formatting: {
        paperSize: '80mm',
        useEscPos: true,
        cutPaper: true,
        feedLines: 4,
        charset: 'UTF-8'
      }
    }
  },
  {
    id: 'restaurant',
    name: 'Restaurant',
    description: 'Receipt format for restaurants and cafes',
    config: {
      header: {
        showLogo: false,
        showCompanyName: true,
        companyNameStyle: 'double',
        showAddress: true,
        showPhone: true,
        showEmail: false,
        showGstin: true,
        showWebsite: false,
        additionalInfo: ['FSSAI No: 1234567890']
      },
      invoiceDetails: {
        showInvoiceNumber: true,
        showDate: true,
        dateFormat: 'dd-MMM-yyyy',
        showTime: true,
        showPaymentMode: true,
        showTerminal: true
      },
      customer: {
        showCustomerName: false,
        showCustomerPhone: false,
        showCustomerAddress: false,
        showCustomerGstin: false
      },
      items: {
        columns: ['item', 'qty', 'rate', 'amount'],
        showItemCode: false,
        showHsnCode: false,
        showDiscountLine: true,
        showTaxInline: false,
        wrapItemNames: true,
        itemNameMaxWidth: 55
      },
      totals: {
        showSubtotal: true,
        showDiscountTotal: true,
        showTaxBreakdown: true,
        taxBreakdownType: 'combined',
        showRounding: false
      },
      footer: {
        showThankYouMessage: true,
        thankYouText: 'Thank You! Bon Appétit!',
        showTermsAndConditions: false,
        showReturnPolicy: false,
        showBarcode: false,
        showQrCode: true,
        qrCodeData: 'payment',
        additionalFooterText: ['Service Charge: Optional', 'Tips are appreciated']
      },
      formatting: {
        paperSize: '80mm',
        useEscPos: true,
        cutPaper: true,
        feedLines: 3,
        charset: 'UTF-8'
      }
    }
  },
  {
    id: 'pharmacy',
    name: 'Pharmacy',
    description: 'Receipt format for pharmacies and medical stores',
    config: {
      header: {
        showLogo: false,
        showCompanyName: true,
        companyNameStyle: 'bold',
        showAddress: true,
        showPhone: true,
        showEmail: false,
        showGstin: true,
        showWebsite: false,
        additionalInfo: ['Drug License No: DL-1234']
      },
      invoiceDetails: {
        showInvoiceNumber: true,
        showDate: true,
        dateFormat: 'dd/MM/yyyy',
        showTime: true,
        showPaymentMode: true
      },
      customer: {
        showCustomerName: true,
        showCustomerPhone: true,
        showCustomerAddress: false,
        showCustomerGstin: false
      },
      items: {
        columns: ['item', 'hsn', 'qty', 'rate', 'amount'],
        showItemCode: false,
        showHsnCode: true,
        showDiscountLine: true,
        showTaxInline: false,
        wrapItemNames: true,
        itemNameMaxWidth: 45
      },
      totals: {
        showSubtotal: true,
        showDiscountTotal: true,
        showTaxBreakdown: true,
        taxBreakdownType: 'separate',
        showRounding: false
      },
      footer: {
        showThankYouMessage: true,
        thankYouText: 'Get Well Soon!',
        showTermsAndConditions: true,
        termsText: ['Goods once sold cannot be returned', 'Keep medicines out of reach of children'],
        showReturnPolicy: false,
        showBarcode: false,
        showQrCode: false
      },
      formatting: {
        paperSize: '80mm',
        useEscPos: false,
        cutPaper: true,
        feedLines: 3,
        charset: 'UTF-8'
      }
    }
  },
  {
    id: 'minimal',
    name: 'Minimal',
    description: 'Compact receipt for small transactions',
    config: {
      header: {
        showLogo: false,
        showCompanyName: true,
        companyNameStyle: 'normal',
        showAddress: false,
        showPhone: true,
        showEmail: false,
        showGstin: false,
        showWebsite: false
      },
      invoiceDetails: {
        showInvoiceNumber: true,
        showDate: true,
        dateFormat: 'dd/MM',
        showTime: true,
        showPaymentMode: false
      },
      customer: {
        showCustomerName: false,
        showCustomerPhone: false,
        showCustomerAddress: false,
        showCustomerGstin: false
      },
      items: {
        columns: ['item', 'qty', 'amount'],
        showItemCode: false,
        showHsnCode: false,
        showDiscountLine: false,
        showTaxInline: false,
        wrapItemNames: false,
        itemNameMaxWidth: 60
      },
      totals: {
        showSubtotal: false,
        showDiscountTotal: false,
        showTaxBreakdown: false,
        taxBreakdownType: 'combined',
        showRounding: false
      },
      footer: {
        showThankYouMessage: true,
        thankYouText: 'Thank You!',
        showTermsAndConditions: false,
        showReturnPolicy: false,
        showBarcode: false,
        showQrCode: false
      },
      formatting: {
        paperSize: '58mm',
        useEscPos: false,
        cutPaper: true,
        feedLines: 2,
        charset: 'UTF-8'
      }
    }
  }
]

// Function to get template by ID
export function getReceiptTemplate(templateId: string): ReceiptTemplate | undefined {
  return defaultTemplates.find(t => t.id === templateId)
}

// Function to get template for specific business type
export function getTemplateForBusiness(businessType: string): ReceiptTemplate {
  const mapping: { [key: string]: string } = {
    'retail': 'retail-basic',
    'gst': 'retail-gst',
    'restaurant': 'restaurant',
    'cafe': 'restaurant',
    'pharmacy': 'pharmacy',
    'medical': 'pharmacy',
    'minimal': 'minimal',
    'small': 'minimal'
  }
  
  const templateId = mapping[businessType.toLowerCase()] || 'retail-basic'
  return getReceiptTemplate(templateId) || defaultTemplates[0]
}

// Printer configurations for different models
export const printerConfigs = {
  generic80mm: {
    charactersPerLine: 42,  // Most common width for 80mm thermal printers
    paperSize: '80mm' as const,
    fontSize: 11
  },
  epson80mm: {
    charactersPerLine: 42,
    paperSize: '80mm' as const,
    fontSize: 11
  },
  star80mm: {
    charactersPerLine: 48,
    paperSize: '80mm' as const,
    fontSize: 12
  },
  generic58mm: {
    charactersPerLine: 32,
    paperSize: '58mm' as const,
    fontSize: 10
  },
  // Compact format for printers with narrow width
  compact80mm: {
    charactersPerLine: 40,
    paperSize: '80mm' as const,
    fontSize: 10
  },
  // Extra narrow for problematic printers
  narrow80mm: {
    charactersPerLine: 38,
    paperSize: '80mm' as const,
    fontSize: 10
  }
}

// Get printer config by name or use default
export function getPrinterConfig(printerType?: string) {
  if (printerType && printerType in printerConfigs) {
    return printerConfigs[printerType as keyof typeof printerConfigs]
  }
  return printerConfigs.generic80mm
}